import { useCallback, useState } from "react";

export interface UseModalReturn<T> {
  isOpen: boolean;
  data: T | undefined;
  open: (data?: T) => void;
  close: () => void;
  create: () => void;
  edit: (item: T) => void;
  isEditing: boolean;
  isCreating: boolean;
}

export interface UseDeleteModalReturn<T> {
  isOpen: boolean;
  data: T | undefined;
  isCritical: boolean;
  open: (item: T, critical?: boolean) => void;
  close: () => void;
  confirmationValue: string;
  setConfirmationValue: (value: string) => void;
  canConfirm: boolean;
  getExpectedValue: (item: T) => string;
}

/**
 * Hook simplificado para manejar estado de modales
 * Elimina la necesidad de forwardRef, useImperativeHandle y refs
 *
 * @example
 * const supplierModal = useModal<Supplier>();
 *
 * <SupplierModal
 *   isOpen={supplierModal.isOpen}
 *   data={supplierModal.data}
 *   onClose={supplierModal.close}
 * />
 *
 * // Abrir modal
 * supplierModal.edit(supplier);
 * supplierModal.create();
 */
export function useModal<T = any>(): UseModalReturn<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<T | undefined>();

  const open = useCallback(
    (newData?: T) => {
      // Actualizar datos directamente si ya está abierto
      setData(newData);
      if (!isOpen) {
        setIsOpen(true);
      }
    },
    [isOpen],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    // Limpiar data inmediatamente al cerrar
    setData(undefined);
  }, []);

  const create = useCallback(() => {
    open(undefined);
  }, [open]);

  const edit = useCallback(
    (item: T) => {
      open(item);
    },
    [open],
  );

  const isEditing = isOpen && data !== undefined;
  const isCreating = isOpen && data === undefined;

  return {
    isOpen,
    data,
    open,
    close,
    create,
    edit,
    isEditing,
    isCreating,
  };
}

/**
 * Hook para manejar modales de confirmación de eliminación
 * Soporta modo crítico que requiere confirmación por nombre/ID
 *
 * @param getIdentifier - Función para extraer el identificador del item (nombre o ID)
 *
 * @example
 * // Modo simple
 * const deleteModal = useDeleteModal<Supplier>((item) => item.name);
 * deleteModal.open(supplier);
 *
 * // Modo crítico
 * deleteModal.open(supplier, true); // Requiere escribir el nombre
 *
 * <DeleteConfirmationModal
 *   isOpen={deleteModal.isOpen}
 *   isCritical={deleteModal.isCritical}
 *   itemName={deleteModal.data?.name}
 *   onClose={deleteModal.close}
 *   onConfirm={handleDelete}
 *   confirmationValue={deleteModal.confirmationValue}
 *   onConfirmationChange={deleteModal.setConfirmationValue}
 *   canConfirm={deleteModal.canConfirm}
 * />
 */
export interface UseDeleteModalReturn<T> {
  isOpen: boolean;
  data: T | undefined;
  isCritical: boolean;
  open: (item: T, critical?: boolean) => void;
  close: () => void;
  confirmationValue: string;
  setConfirmationValue: (value: string) => void;
  canConfirm: boolean;
  getExpectedValue: (item: T) => string;
}

export function useDeleteModal<T = any>(
  getIdentifier: (item: T) => string = (item: any) =>
    item?.name || item?.id || "",
): UseDeleteModalReturn<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<T | undefined>();
  const [isCritical, setIsCritical] = useState(false);
  const [confirmationValue, setConfirmationValue] = useState("");

  const open = useCallback((item: T, critical: boolean = false) => {
    setData(item);
    setIsCritical(critical);
    setConfirmationValue("");
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Limpiar data inmediatamente al cerrar
    setData(undefined);
    setIsCritical(false);
    setConfirmationValue("");
  }, []);

  const getExpectedValue = useCallback(
    (item: T) => {
      return getIdentifier(item);
    },
    [getIdentifier],
  );

  const canConfirm = (() => {
    // Si no es crítico, siempre puede confirmar
    if (!isCritical) return true;

    // Si es crítico pero no hay data, no puede confirmar
    if (!data) return false;

    // Obtener el valor esperado y comparar (case-sensitive y trim)
    const expectedValue = getExpectedValue(data).trim();
    const inputValue = confirmationValue.trim();

    //  Comparación estricta
    return expectedValue === inputValue;
  })();

  return {
    isOpen,
    data,
    isCritical,
    open,
    close,
    confirmationValue,
    setConfirmationValue,
    canConfirm,
    getExpectedValue,
  };
}
